import { useState, useCallback } from 'react';
import { clienteApi } from '../api/clienteApi';
import { showErrorToast, showSuccessToast } from '../utils/errorHandler';

/**
 * FastAPI devuelve `nombre`; Laravel (alias SQL) devuelve `nombre_cliente`.
 * Algunas respuestas envuelven el listado en { data: [...] }.
 */
function extractClienteArray(payload) {
  if (payload == null) return [];
  if (Array.isArray(payload)) return payload;
  if (typeof payload === 'object') {
    if (Array.isArray(payload.data)) return payload.data;
    if (payload.data && typeof payload.data === 'object' && Array.isArray(payload.data.data)) {
      return payload.data.data;
    }
  }
  return [];
}

function normalizeClienteListItem(c) {
  if (!c || typeof c !== 'object') return c;
  const nameFromApi =
    c.nombre ?? c.nombre_cliente ?? c.razon_social ?? c.razonSocial;
  const label = nameFromApi == null ? '' : String(nameFromApi).trim();
  const display = label || (c.id != null ? `Cliente #${c.id}` : '');
  return {
    ...c,
    nombre_cliente: display,
    nombre: label || display,
  };
}

const mapClientesResponse = (arr) =>
  Array.isArray(arr) ? arr.map(normalizeClienteListItem) : [];

/**
 * Hook personalizado para gestión de clientes
 * Proporciona estado y funciones para operaciones CRUD de clientes con paginación
 */
export const useClientes = () => {
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    perPage: 10,
    total: 0,
    lastPage: 1
  });

  /**
   * Obtiene clientes con filtros y paginación
   */
  const fetchClientes = useCallback(async (idUser = null, idCliente = null, page = 1, perPage = 10) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await clienteApi.getAll(idUser, idCliente, page, perPage);
      
      // Manejar respuesta con paginación
      if (response.data.data && Array.isArray(response.data.data)) {
        setClientes(mapClientesResponse(response.data.data));
        setPagination({
          currentPage: response.data.current_page || page,
          perPage: response.data.per_page || perPage,
          total: response.data.total || 0,
          lastPage: response.data.last_page || 1
        });
      } else if (Array.isArray(response.data)) {
        // Formato simple sin paginación
        setClientes(mapClientesResponse(response.data));
        setPagination({
          currentPage: 1,
          perPage: response.data.length,
          total: response.data.length,
          lastPage: 1
        });
      }
      
      return response.data;
    } catch (err) {
      const errorMessage = showErrorToast(err, 'Error al cargar clientes');
      setError(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Obtiene un cliente específico por ID
   */
  const fetchClienteById = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await clienteApi.getById(id);
      return response.data;
    } catch (err) {
      const errorMessage = showErrorToast(err, 'Error al cargar el cliente');
      setError(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Crea un nuevo cliente
   */
  const createCliente = useCallback(async (data) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await clienteApi.create(data);
      showSuccessToast('Cliente creado exitosamente');
      
      // Refrescar la lista de clientes
      await fetchClientes();
      
      return response.data;
    } catch (err) {
      const errorMessage = showErrorToast(err, 'Error al crear el cliente');
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchClientes]);

  /**
   * Actualiza un cliente existente
   */
  const updateCliente = useCallback(async (id, data) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await clienteApi.update(id, data);
      showSuccessToast('Cliente actualizado exitosamente');
      
      // Refrescar la lista de clientes
      await fetchClientes();
      
      return response.data;
    } catch (err) {
      const errorMessage = showErrorToast(err, 'Error al actualizar el cliente');
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchClientes]);

  /**
   * Elimina un cliente
   */
  const deleteCliente = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    
    try {
      await clienteApi.delete(id);
      showSuccessToast('Cliente eliminado exitosamente');
      
      // Refrescar la lista de clientes
      await fetchClientes();
      
      return true;
    } catch (err) {
      const errorMessage = showErrorToast(err, 'Error al eliminar el cliente');
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchClientes]);

  /**
   * Obtiene clientes por usuario con paginación
   */
  const fetchClientesByUser = useCallback(async (userId, page = 1, perPage = 10) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await clienteApi.getByUser(userId, page, perPage);
      return response.data;
    } catch (err) {
      const errorMessage = showErrorToast(err, 'Error al cargar clientes del usuario');
      setError(errorMessage);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Obtiene TODOS los clientes por usuario sin paginación (para comboboxes)
   */
  const fetchAllClientesByUser = useCallback(async (userId) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await clienteApi.getAllByUser(userId);
      const list = mapClientesResponse(
        extractClienteArray(response.data)
      );
      setClientes(list);
      return list;
    } catch (err) {
      const errorMessage = showErrorToast(err, 'Error al cargar clientes del usuario');
      setError(errorMessage);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Obtiene TODOS los clientes sin filtros ni paginación (para comboboxes)
   */
  const fetchAllClientes = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await clienteApi.getAllClientes();
      const list = mapClientesResponse(extractClienteArray(response.data));
      setClientes(list);
      return list;
    } catch (err) {
      const errorMessage = showErrorToast(err, 'Error al cargar todos los clientes');
      setError(errorMessage);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Obtiene clientes asociados a un proyecto específico (query parameter)
   */
  const fetchClientesByProyecto = useCallback(async (proyectoId) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await clienteApi.getByProyecto(proyectoId);

      const clientesData = mapClientesResponse(extractClienteArray(response.data));

      setClientes(clientesData);
      return clientesData;
    } catch (err) {
      const errorMessage = showErrorToast(err, 'Error al cargar clientes del proyecto');
      setError(errorMessage);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Obtiene clientes asociados a un proyecto específico usando resource nesting (para carga masiva)
   */
  const fetchClientesByProyectoRest = useCallback(async (proyectoId) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await clienteApi.getByProyectoRest(proyectoId);
      const list = mapClientesResponse(extractClienteArray(response.data));
      setClientes(list);
      return list;
    } catch (err) {
      const errorMessage = showErrorToast(err, 'Error al cargar clientes del proyecto');
      setError(errorMessage);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Cambia de página en la paginación
   */
  const changePage = useCallback(async (page) => {
    await fetchClientes(null, null, page, pagination.perPage);
  }, [fetchClientes, pagination.perPage]);

  // Cargar clientes al montar el componente
  // NOTA: No se cargan automáticamente los clientes
  // Se cargan bajo demanda cuando se necesiten en los componentes

  return {
    // Estado
    clientes,
    loading,
    error,
    pagination,
    
    // Funciones
    fetchClientes,
    fetchClienteById,
    createCliente,
    updateCliente,
    deleteCliente,
    fetchClientesByUser,
    fetchAllClientesByUser,
    fetchAllClientes,
    fetchClientesByProyecto,
    fetchClientesByProyectoRest,
    changePage,
    
    // Función para limpiar errores
    clearError: () => setError(null),
  };
};

export default useClientes;
